const COUNTRY_CODE_PARAM = 'countryCode'
const LANGUAGE_PARAM = 'language'
const LOCALE_PARAM = 'locale'
const DEFAULT_COUNTRY_CODE = 'NL'
const DEFAULT_LANGUAGE = 'en'
const COUNTRY_CODE_PATTERN = /^[a-z]{2}$/i
const LANGUAGE_PATTERN = /^[a-z]{2}$/i
/** Matches locale path segments like /en-NL/ or /nl-nl/, capturing language then country */
const LOCALE_PATH_PATTERN = /^\/([a-z]{2})-([a-z]{2})(?:\/|$)/i
/** Matches a storefront locale prefix, e.g. "en-nl" */
const LOCALE_PATTERN = /^[a-z]{2}-[a-z]{2}$/
/** Matches full locale strings like "en-NL" or "nl-nl" */
const LOCALE_STRING_PATTERN = /^[a-z]{2}-([a-z]{2})$/i

export function normalizeCountryCode(
  value?: string | null,
): string | undefined {
  const normalized = value?.replace(/['"]/g, '').trim().toUpperCase()
  return normalized && COUNTRY_CODE_PATTERN.test(normalized)
    ? normalized
    : undefined
}

function getCurrentUrl(): URL | undefined {
  if (typeof window === 'undefined') return undefined
  return new URL(window.location.href)
}

function getCountryFromQueryParam(url: URL): string | undefined {
  return normalizeCountryCode(url.searchParams.get(COUNTRY_CODE_PARAM))
}

function getCountryFromLocalePath(url: URL): string | undefined {
  const match = url.pathname.match(LOCALE_PATH_PATTERN)?.[2]
  return normalizeCountryCode(match)
}

/**
 * Accepts a bare language code ("nl") or a full locale ("en-NL") and returns
 * the lowercase language half.
 */
export function normalizeLanguage(value?: string | null): string | undefined {
  const normalized = value?.replace(/['"]/g, '').trim().toLowerCase()
  if (!normalized) return undefined

  const [language] = normalized.split('-')
  return LANGUAGE_PATTERN.test(language) ? language : undefined
}

function getLanguageFromLocalePath(url: URL): string | undefined {
  return normalizeLanguage(url.pathname.match(LOCALE_PATH_PATTERN)?.[1])
}

/**
 * Resolves the UI language.
 *
 * Resolution order:
 * 1. Language segment of the host page's URL locale path (`/nl-nl/…` → `nl`)
 * 2. `language` URL query param (`?language=nl`)
 * 3. Explicit `language` prop from the host
 * 4. Default: `"en"`
 *
 * The page's own locale outranks both because it is what the customer is
 * actually reading: a host that hardcodes or caches a stale `language` prop
 * would otherwise render the booking tool in a different language from the page
 * around it.
 *
 * The value is not clamped to `supportedLanguages` — `@susu/i18n` already falls
 * back per key, and the resolved code is reported to analytics as-is
 * (`localeVisited`), so narrowing it here would lose information.
 *
 * @param language - language/locale prop, e.g. "nl" or "en-NL"
 * @param url - injectable for testing; defaults to window.location
 */
export function resolveLanguage(
  language?: string,
  url = getCurrentUrl(),
): string {
  if (url) {
    const fromPath = getLanguageFromLocalePath(url)
    if (fromPath) return fromPath

    const fromParam = normalizeLanguage(url.searchParams.get(LANGUAGE_PARAM))
    if (fromParam) return fromParam
  }

  return normalizeLanguage(language) ?? DEFAULT_LANGUAGE
}

/**
 * Maps a resolved language code onto a BCP 47 tag for `Intl` date formatting.
 *
 * Weekday and month names are locale data, not copy — they come from `Intl`
 * rather than `en.json`, the same way the legacy tool read them from a dayjs
 * locale. `zh` is pinned to `zh-CN` so the mainland conventions are explicit
 * instead of left to the runtime's default region; every other supported
 * language is a bare code `Intl` resolves on its own.
 *
 * `resolveLanguage` guarantees a well-formed two-letter code, so this never
 * hands `Intl` a tag that would throw.
 */
export function resolveIntlLocale(language = DEFAULT_LANGUAGE): string {
  return language === 'zh' ? 'zh-CN' : language
}

/**
 * Derives a country code from a full locale string only (e.g. "en-NL" → "NL").
 * Bare language codes like "en" or "nl" are intentionally ignored here —
 * use the explicit `location` parameter of resolveCountryCode instead.
 */
export function getCountryFromLocaleProp(
  language?: string,
): string | undefined {
  if (!language) return undefined
  const localeMatch = language.match(LOCALE_STRING_PATTERN)?.[1]
  return localeMatch ? normalizeCountryCode(localeMatch) : undefined
}

/**
 * Resolves the initial country code for the store locator.
 *
 * Resolution order:
 * 1. `countryCode` URL query param (e.g. `?countryCode=NL`)
 * 2. Country segment of the URL locale path (e.g. `/en-NL/` → `NL`)
 * 3. Country code from the full locale `language` string (e.g. `"en-NL"` → `NL`)
 * 4. Explicit `location` value from the store (e.g. `"nl"` → `"NL"`)
 * 5. Default: `"NL"`
 *
 * @param language - locale/language prop, e.g. "en-NL" or "en"
 * @param location - explicit country code from the store, e.g. "nl"
 * @param url - injectable for testing; defaults to window.location
 */
export function resolveCountryCode(
  language?: string,
  location?: string,
  url = getCurrentUrl(),
): string {
  if (url) {
    const fromParam = getCountryFromQueryParam(url)
    if (fromParam) return fromParam

    const fromPath = getCountryFromLocalePath(url)
    if (fromPath) return fromPath
  }

  const fromLocale = getCountryFromLocaleProp(language)
  if (fromLocale) return fromLocale

  const fromLocation = normalizeCountryCode(location)
  if (fromLocation) return fromLocation

  return DEFAULT_COUNTRY_CODE
}

/**
 * Resolves the country code and validates it against the loaded countries list.
 * Falls back to Netherlands if the resolved code is absent from the list.
 */
export function resolveAndValidateCountryCode(
  availableCountryCodes: string[],
  language?: string,
  location?: string,
  url = getCurrentUrl(),
): string {
  const resolved = resolveCountryCode(language, location, url)
  const match = availableCountryCodes.find(
    (c) => c.toUpperCase() === resolved.toUpperCase(),
  )
  if (match) return match

  const nl = availableCountryCodes.find(
    (c) => c.toUpperCase() === DEFAULT_COUNTRY_CODE,
  )
  return nl ?? availableCountryCodes[0] ?? DEFAULT_COUNTRY_CODE
}

/**
 * Accepts a locale in any casing and returns it lowercased, or `undefined`
 * when it is not a well-formed `xx-xx`.
 */
function normalizeLocale(value?: string | null): string | undefined {
  const normalized = value?.replace(/['"]/g, '').trim().toLowerCase()
  return normalized && LOCALE_PATTERN.test(normalized) ? normalized : undefined
}

function getLocaleFromLocalePath(url: URL): string | undefined {
  const match = url.pathname.match(LOCALE_PATH_PATTERN)
  return match ? normalizeLocale(`${match[1]}-${match[2]}`) : undefined
}

/**
 * Resolves the storefront locale prefix (e.g. `"en-nl"`) for the links this
 * package builds back into the storefront — the reschedule confirmation's
 * store-page redirect.
 *
 * Resolution order:
 * 1. Locale path of the host page's URL (`/en-NL/…` → `"en-nl"`)
 * 2. `locale` URL query param (`?locale=en-nl`) — the key the legacy tool's
 *    deep links already carry
 * 3. Full-locale `language` prop from the host (`"en-NL"` → `"en-nl"`)
 * 4. Composed from `resolveLanguage` + `resolveCountryCode`
 *
 * Same order as `resolveLanguage`, for the same reason: the page's own locale is
 * what the customer is actually reading, so it outranks a prop a host may have
 * hardcoded or cached.
 *
 * Validated on shape rather than against a list of live locales — that is what
 * the storefront's own router does with the segment, so anything returned here
 * is a prefix it will accept. Steps 1-3 capture a locale the customer actually
 * arrived on; only the composed fallback can pair two halves the storefront
 * does not publish together (`nl` + `be`), and it reads them from the host
 * props, not the store, whose `location` the reschedule flow overwrites with the
 * booked store's country.
 *
 * Always resolves: the composition bottoms out at the resolver defaults, so
 * callers never have to handle a missing prefix.
 *
 * @param language - language/locale prop, e.g. "en-NL" or "en"
 * @param location - explicit country code from the host, e.g. "nl"
 * @param url - injectable for testing; defaults to window.location
 */
export function resolveLocale(
  language?: string,
  location?: string,
  url = getCurrentUrl(),
): string {
  if (url) {
    const fromPath = getLocaleFromLocalePath(url)
    if (fromPath) return fromPath

    const fromParam = normalizeLocale(url.searchParams.get(LOCALE_PARAM))
    if (fromParam) return fromParam
  }

  const fromProp = normalizeLocale(language)
  if (fromProp) return fromProp

  const resolvedLanguage = resolveLanguage(language, url)
  const resolvedCountry = resolveCountryCode(language, location, url)
  return `${resolvedLanguage}-${resolvedCountry}`.toLowerCase()
}
